import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

const STORAGE_KEY = 'cms-hub:cart';

type CartItem = {
  package_id: string;
  key: string;
  /** For the sentinel 'balance-custom' package this is the user-chosen
   *  dollar amount in cents. Fixed-price packages ignore it. */
  amount_cents?: number;
};

function loadInitial(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

/** Local-only shopping cart persisted to localStorage. Items are a flat
 *  list — duplicates are allowed so "+1 editor × 3" comes out naturally.
 *  For balance-custom items, amount_cents carries the dollar value. */
export function useCart() {
  const [items, setItems] = useState<CartItem[]>(() => loadInitial());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* quota / private mode — ignore */
    }
  }, [items]);

  const add = useCallback((package_id: string, amount_cents?: number) => {
    setItems((prev) => [
      ...prev,
      {
        package_id,
        amount_cents,
        key: `${package_id}:${amount_cents ?? ''}:${Date.now()}:${Math.random()}`,
      },
    ]);
  }, []);

  const remove = useCallback((key: string) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  const removeAll = useCallback((package_id: string, amount_cents?: number) => {
    setItems((prev) =>
      prev.filter((i) =>
        amount_cents == null ? i.package_id !== package_id : !(i.package_id === package_id && i.amount_cents === amount_cents),
      ),
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = (package_id: string) => items.filter((i) => i.package_id === package_id).length;

  return { items, add, remove, removeAll, clear, count };
}

// ---------- Cart checkout + detail hooks ----------

export type CartCheckoutResponse = {
  cart_id: string;
  wallet: {
    asset: string;
    network: string;
    network_label: string;
    address: string;
    memo: string | null;
  };
  total_amount: string;
  currency: string;
};

export function useCartCheckoutCrypto() {
  return useMutation({
    mutationFn: (body: {
      items: Array<{ package_id: string; amount_cents?: number }>;
      wallet_id: string;
    }) => api.post<CartCheckoutResponse>('/api/app/billing/checkout-cart-crypto', body),
  });
}

export type CartDetail = {
  cart: {
    id: string;
    organization_id: string;
    user_id: string;
    payment_method: string;
    status: string;
    total_amount_usd_cents: number;
    crypto_asset: string | null;
    crypto_network: string | null;
    crypto_network_label: string | null;
    crypto_address: string | null;
    crypto_memo: string | null;
    crypto_expected_amount: string | null;
    crypto_txid: string | null;
    crypto_txid_submitted_at: number | null;
    crypto_explorer_url: string | null;
    verification_notes: string | null;
    paid_at: number | null;
    created_at: number;
  };
  items: Array<{
    id: string;
    package_id: string;
    package_name: string | null;
    amount_paid: number | null;
    status: string;
    kind: string | null;
    duration_days: number | null;
  }>;
};

export function useCartDetail(cartId: string | undefined) {
  return useQuery({
    queryKey: ['cart', cartId],
    queryFn: () => api.get<CartDetail>(`/api/app/billing/carts/${cartId}`),
    enabled: !!cartId,
    refetchInterval: (q) => {
      const status = q.state.data?.cart.status;
      return status === 'paid' || status === 'failed' ? false : 10_000;
    },
  });
}

export function useSubmitCartTxid(cartId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (txid: string) =>
      api.post<{ submitted: boolean }>(`/api/app/billing/carts/${cartId}/submit-txid`, { txid }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cart', cartId] }),
  });
}
